import {
  useEffect,
  createContext,
  useRef,
  useContext,
  useMemo,
  useCallback,
} from "react";

import { debounce, keys } from "@s-libs/micro-dash";
import cuid from "cuid";
import PropTypes from "prop-types";

import useGetPreviousUrl from "@/eventStream/useGetPreviousUrl";
import useGetUrlQueryParameters from "@/eventStream/useGetUrlQueryParameters";
import {
  propertyNames,
  dimensionPropertyNames,
} from "@/eventStream/withTrackEvent/withTrackEvent.constants";
import fetchJson from "@/remoteState/fetchJson";
import useUser from "@/users/useUser";
import getApplicationEnvironment from "@/utils/getApplicationEnvironment";
import {
  getExternalReferrer,
  getInternalReferrer,
  updateReferrer,
} from "@/utils/referrer";
import { EVENT_BATCH_DEBOUNCE_TIME_MS } from "./useTracker.constants";

const EventTrackingMethodsContext = createContext({});
const OWN_HOST = process.env.NEXT_PUBLIC_SER_DAVOS_URL || "";
const SER_DAVOS_URL =
  process.env.NEXT_PUBLIC_SER_DAVOS_URL || `${OWN_HOST}/ser-davos`;

/**
 * Check that the event stream data is formatted properly, and that property
 * names match those from the types lists. Log exceptions.
 *
 * @param {object} - eventStreamData
 */
export const checkStreamProperties = (eventStreamData) => {
  const arrOfProperties = keys(eventStreamData);
  const arrOfDimensionProperties = keys(eventStreamData.dimension_properties);
  const exceptions = [];

  arrOfProperties.forEach((property) => {
    if (!propertyNames.includes(property)) {
      const exception = {
        errorType: "Invalid property name",
        invalidProperty: property,
        errorMessage: `Please make sure to include the property name /${property}/ on the propertyNames constant, which is also attached to this error object`,
        allowedPropertiesNames: propertyNames,
      };
      exceptions.push(exception);
    }
  });
  arrOfDimensionProperties.forEach((dimensionProperty) => {
    if (!dimensionPropertyNames.includes(dimensionProperty)) {
      const exception = {
        errorType: "Invalid dimension property",
        invalidProperty: dimensionProperty,
        errorMessage: `Please make sure to include the dimension property /${dimensionProperty}/ on the dimensionProperty constant, which is also attached to this error object`,
        allowedPropertiesNames: propertyNames,
      };
      exceptions.push(exception);
    }
  });

  if (exceptions.length) {
    console.error("Event-Stream exceptions", exceptions);
    return exceptions;
  }
  return null;
};

/**
 * Get event name for event stream.
 *
 * For example:
 * ('Object', 'Prefix', 'Action') becomes 'Object Prefix Action'
 * ('Object', null, 'Action') becomes 'Object Action'
 * (null, null, ' Action ') becomes 'Action'
 *
 * @param {objectType} - object type for the event
 * @param {actionPrefix} - the prefix of the action the event is tracking
 * @param {actionName} - name of the action being tracked
 *
 */
export const getEventNameForEventStream = (
  objectType = "",
  actionPrefix = "",
  actionName = "",
) => {
  return `${objectType || ""} ${actionPrefix || ""} ${actionName || ""}`
    .replace(/\s+/g, " ")
    .trim();
};

export const EventTrackingProvider = ({ children }) => {
  const batch = useRef([]);
  const { data: user } = useUser();
  const getPreviousUrl = useGetPreviousUrl();
  const getUrlQueryParameters = useGetUrlQueryParameters();
  const createFerryPostRequest = useCallback(() => {
    if (!batch.current.length) return;
    const data = batch.current;
    batch.current = [];
    return new Request(`${SER_DAVOS_URL}/ferry`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: { "Content-Type": "application/json" },
      // We use `keepalive` to help ensure that analytics events do not get
      // dropped, even if the user closes the tab or navigates away.
      // > The `keepalive` option can be used to allow the request to outlive
      // > the page.
      // https://developer.mozilla.org/en-US/docs/Web/API/fetch#keepalive
      keepalive: true,
    });
  }, []);

  const sendBatchToStream = useCallback(() => {
    const ferryPostRequest = createFerryPostRequest();
    if (ferryPostRequest) {
      return fetchJson(ferryPostRequest);
    }
  }, [createFerryPostRequest]);

  useEffect(() => {
    updateReferrer();
  }, []);

  // When the user leaves the page, send any leftover events that are still in the queue
  useEffect(() => {
    window.addEventListener("beforeunload", sendBatchToStream);
    return () => window.removeEventListener("beforeunload", sendBatchToStream);
  }, [sendBatchToStream]);

  // We don't want to send a bunch of individual HTTP calls to Ser Davos.
  // Instead of doing that, wait until all passengers have boarded.
  // Don't wait any longer than two seconds, though. Smugglers make no
  // guarantees.
  const setSail = useMemo(
    () => debounce(sendBatchToStream, EVENT_BATCH_DEBOUNCE_TIME_MS),
    [sendBatchToStream],
  );

  const trackAction = useCallback(
    (dataObj) => {
      const universalDataProperties = () => {
        const date = new Date().toISOString();
        return {
          date_created: date.replace("T", " ").slice(0, date.indexOf(".")),
          id: cuid(),
          platform_version: 0.0,
          platform: "lyceum",
          user_id: user?.id,
          user_unique_id: user?.unique_id,
        };
      };

      const eventInfo = {
        ...dataObj,
        ...universalDataProperties(),
        dimension_properties: {
          ...dataObj.dimension_properties,
          current_url: globalThis.location?.href,
          previous_url: getPreviousUrl(),
          external_referrer: getExternalReferrer(),
          internal_referrer: getInternalReferrer(),
          url_query_parameters: getUrlQueryParameters,
        },
      };

      const kinesisPayload = {
        payload: eventInfo,
        stream_name: "event-stream",
        stream_type: "firehose",
      };

      batch.current.push(kinesisPayload);

      const { isProductionEnv } = getApplicationEnvironment();
      // Only send events if on production.
      if (isProductionEnv) {
        setSail();
      } else {
        // Check all property keys against approved list. Log exceptions.
        checkStreamProperties(eventInfo);
        // eslint-disable-next-line no-console
        console.log("Event-Stream", kinesisPayload);
        batch.current = [];
      }
    },
    [user, getPreviousUrl, setSail],
  );

  const methods = useMemo(
    () => ({ trackAction, getEventNameForEventStream }),
    [trackAction],
  );

  return (
    <EventTrackingMethodsContext.Provider value={methods}>
      {children}
    </EventTrackingMethodsContext.Provider>
  );
};

EventTrackingProvider.propTypes = {
  children: PropTypes.node,
};

const useTracker = () => {
  const { trackAction, getEventNameForEventStream } = useContext(
    EventTrackingMethodsContext,
  );
  return { trackAction, getEventNameForEventStream };
};

export default useTracker;
